/*
 * Copyright (c) Nexign, JSC, 2022.
 */
/**
 * Reports constructor template rename modal
 *
 * @author Ilya Babanov
 * @version 1.22.0
 * @since 1.22.0
 */

import { useEffect } from 'react';
import type { FC } from 'react';

import { Formik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';

import { ERequestStatus } from '@/main/types/globalTypes';
import { icons } from '@assets/index';
import { Button } from '@components/common';
import { ImgStyled } from '@components/common/Block/Img';
import FormFieldInput from '@modules/form/components/FormFieldInput';
import {
    makeSelectPageReportsConstructorTranslation,
    makeSelectTranslationCommon,
} from '@modules/internationalization/intSelectors';
import { Loader } from '@modules/loader/components/Loader';
import Modal from '@modules/modal/components/Modal';
import { ModalTitleStyled, ModalTitleTextStyled } from '@modules/modal/components/styled/ModalStyled';
import { modalSetStatusAction } from '@modules/modal/modalActions';
import { EModalStatus } from '@modules/modal/types/modalTypes';
import { stringFormatParams } from '@utils/stringFormat';

import { EMode, ETemplateRenameField } from '../constants';
import { useLazyGetConstructorTemplatesQuery, useUpdateReportTemplateNameMutation } from '../store';
import type { ITemplateRenameForm, TTemplateId } from '../types';
import { setTemplateRenameValidationSchema } from '../utils/validation';
import { ConstructorRequestStatusModal } from './ConstructorRequestStatusModal';

interface IProps {
    mode: EMode;
    templateId: TTemplateId;
    onCloseModal: () => void;
}

export const TemplateNameModal: FC<IProps> = ({ mode, templateId, onCloseModal }) => {
    const dispatch = useDispatch();

    const tCommon = useSelector(makeSelectTranslationCommon());
    const {
        start: tStart,
        success: tSuccess,
        failure: tFailure,
    } = useSelector(makeSelectPageReportsConstructorTranslation());

    const [getConstructorTemplates] = useLazyGetConstructorTemplatesQuery();

    const validationSchema = setTemplateRenameValidationSchema(tCommon.form);
    const isSaveMode = mode === EMode.SAVE;

    const [renameTemplate, { error, isError, isLoading, isSuccess, data }] = useUpdateReportTemplateNameMutation();

    useEffect(() => {
        if (isSuccess) {
            dispatch(
                modalSetStatusAction({
                    status: EModalStatus.success,
                }),
            );
        }
    }, [dispatch, isSuccess]);

    useEffect(() => {
        if (isError) {
            dispatch(
                modalSetStatusAction({
                    status: EModalStatus.failure,
                }),
            );
        }
    }, [dispatch, isError]);

    const handleSubmit = (formData: ITemplateRenameForm) => {
        renameTemplate({ templateId, name: formData.name })
            .unwrap()
            .then(() => getConstructorTemplates(0));
    };

    const initialValues: ITemplateRenameForm = {
        name: '',
    };

    return (
        <Modal dataName="reports-template-rename-modal" style={{ padding: '24px' }} width="480px">
            <Modal.Status status={EModalStatus.start}>
                <Modal.Title className="modal-popup-title__base">
                    <ModalTitleStyled>
                        <ModalTitleTextStyled>
                            {isSaveMode ? tStart.saveTemplateModalTitle : tStart.renameTemplateModalTitle}
                        </ModalTitleTextStyled>
                        <ImgStyled height="16px" src={icons.cancelSmallIcon} width="16px" onClick={onCloseModal} />
                    </ModalTitleStyled>
                </Modal.Title>
                <Formik<ITemplateRenameForm>
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                >
                    {({ dirty, isValid, submitForm, errors, touched }) => (
                        <Loader showLoader={isLoading}>
                            <Modal.Body style={{ maxHeight: 'initial', paddingTop: '16px' }}>
                                <FormFieldInput
                                    dataName="template-name"
                                    hideValidation={true}
                                    hintMessage={touched.name && errors.name ? errors.name : ''}
                                    label={tStart.fields.templateName.label}
                                    name={ETemplateRenameField.name}
                                    placeholder={tStart.fields.templateName.placeholder}
                                    required={true}
                                />
                            </Modal.Body>
                            <Modal.Control className="modal-popup-control__base">
                                <Button disabled={!isValid || !dirty} type="submit" onClick={submitForm}>
                                    {tCommon.modal.save}
                                </Button>
                            </Modal.Control>
                        </Loader>
                    )}
                </Formik>
            </Modal.Status>
            <Modal.Status status={EModalStatus.success}>
                <ConstructorRequestStatusModal
                    mainText={stringFormatParams(tSuccess.renameTemplateModalText, { name: data })}
                    mode={ERequestStatus.success}
                    title={tSuccess.renameTemplateModalTitle}
                    onCloseModal={onCloseModal}
                />
            </Modal.Status>
            <Modal.Status status={EModalStatus.failure}>
                <ConstructorRequestStatusModal
                    mode={ERequestStatus.fail}
                    requestError={error}
                    title={tFailure.renameTemplateModalTitle}
                />
            </Modal.Status>
        </Modal>
    );
};
